/**
 * Targeting state: the placed target, the chosen delivery mode, the parameters
 * being planned with, and the latest solution.
 *
 * A store rather than component state for the same reason missionStore is: the
 * AttackDock plans the solution, and a map layer draws its geometry, and neither
 * should own it. Both read the one target and the one solution here, so the card
 * and the map can never disagree about where the operator aimed.
 *
 * The solution is retired the instant its inputs move — a new target, or a mode
 * switch — because a dive solution drawn against a target that has since been
 * dragged is a picture of an attack the numbers no longer describe. Stale
 * geometry on a weapon-delivery map is the one failure this store exists to
 * prevent, so `place` and `setMode` both clear it.
 */

import { create } from 'zustand';

import type { DiveSolution, ReleaseSolution } from '@/api/attack';

export interface TargetPoint {
  lat: number;
  lon: number;
}

export type AttackMode = 'dive' | 'release';

/**
 * A computed solution, tagged with which mode produced it and whether wind was
 * actually supplied. `windKnown` is recorded here rather than inferred from the
 * correction distance: a genuine zero correction (wind dead down the run) and
 * an uncorrected solution (no wind given) are different claims, and the readout
 * and the map must be able to tell them apart.
 */
export type AttackSolution =
  | { kind: 'dive'; windKnown: boolean; data: DiveSolution }
  | { kind: 'release'; windKnown: boolean; data: ReleaseSolution };

export interface DiveParams {
  diveAngleDeg: number;
  pushoverAltM: number;
  /** Null means "use the bearing from the aircraft to the target" (auto). */
  approachBearingDeg: number | null;
}

export interface ReleaseParams {
  releaseAltM: number;
  groundSpeedMs: number;
}

const DEFAULT_DIVE: DiveParams = {
  diveAngleDeg: 30,
  pushoverAltM: 300,
  approachBearingDeg: null,
};

const DEFAULT_RELEASE: ReleaseParams = {
  releaseAltM: 120,
  groundSpeedMs: 20,
};

interface TargetingState {
  /** Whether the next map click sets the target, mirroring a zone-draw arm. */
  placing: boolean;
  target: TargetPoint | null;
  mode: AttackMode;
  dive: DiveParams;
  release: ReleaseParams;
  solution: AttackSolution | null;

  armPlacement: () => void;
  cancelPlacement: () => void;
  place: (lat: number, lon: number) => void;
  setMode: (mode: AttackMode) => void;
  setDive: (changes: Partial<DiveParams>) => void;
  setRelease: (changes: Partial<ReleaseParams>) => void;
  setSolution: (solution: AttackSolution | null) => void;
  clearTarget: () => void;
  reset: () => void;
}

export const useTargetingStore = create<TargetingState>((set) => ({
  placing: false,
  target: null,
  mode: 'dive',
  dive: DEFAULT_DIVE,
  release: DEFAULT_RELEASE,
  solution: null,

  armPlacement: () => set({ placing: true }),
  cancelPlacement: () => set({ placing: false }),
  // A new target invalidates the old geometry: the solution is dropped so the
  // map never shows a run-in aimed at where the target used to be.
  place: (lat, lon) => set({ target: { lat, lon }, placing: false, solution: null }),
  // Switching dive<->drop invalidates a solution of the other kind.
  setMode: (mode) => set((state) => (mode === state.mode ? {} : { mode, solution: null })),
  setDive: (changes) => set((state) => ({ dive: { ...state.dive, ...changes } })),
  setRelease: (changes) => set((state) => ({ release: { ...state.release, ...changes } })),
  setSolution: (solution) => set({ solution }),
  clearTarget: () => set({ target: null, solution: null, placing: false }),
  reset: () =>
    set({
      placing: false,
      target: null,
      mode: 'dive',
      dive: DEFAULT_DIVE,
      release: DEFAULT_RELEASE,
      solution: null,
    }),
}));

/**
 * Watch the geometry the map draws — the target and the solution — without
 * React. Fires only when one of those two changes identity, so editing a
 * parameter in the dock does not redraw the map until a new solution lands.
 */
export function subscribeToTargeting(
  callback: (geometry: { target: TargetPoint | null; solution: AttackSolution | null }) => void,
): () => void {
  let previous = useTargetingStore.getState();
  callback({ target: previous.target, solution: previous.solution });

  return useTargetingStore.subscribe((state) => {
    if (state.target === previous.target && state.solution === previous.solution) return;
    previous = state;
    callback({ target: state.target, solution: state.solution });
  });
}

/**
 * Actions at module scope, stable forever — components call these instead of
 * subscribing to the store for a setter, the missionStore convention.
 */
export const targetingActions = {
  armPlacement: () => useTargetingStore.getState().armPlacement(),
  cancelPlacement: () => useTargetingStore.getState().cancelPlacement(),
  place: (lat: number, lon: number) => useTargetingStore.getState().place(lat, lon),
  setMode: (mode: AttackMode) => useTargetingStore.getState().setMode(mode),
  setDive: (changes: Partial<DiveParams>) => useTargetingStore.getState().setDive(changes),
  setRelease: (changes: Partial<ReleaseParams>) =>
    useTargetingStore.getState().setRelease(changes),
  setSolution: (solution: AttackSolution | null) =>
    useTargetingStore.getState().setSolution(solution),
  clearTarget: () => useTargetingStore.getState().clearTarget(),
  isPlacing: (): boolean => useTargetingStore.getState().placing,
  reset: () => useTargetingStore.getState().reset(),
};

/* Selectors — one question each, so a parameter edit does not re-render a map
 * hook that only cares about the target. */

export function useTargetingTarget(): TargetPoint | null {
  return useTargetingStore((state) => state.target);
}

export function useTargetingMode(): AttackMode {
  return useTargetingStore((state) => state.mode);
}

export function useTargetingPlacing(): boolean {
  return useTargetingStore((state) => state.placing);
}

export function useTargetingSolution(): AttackSolution | null {
  return useTargetingStore((state) => state.solution);
}

export function useDiveParams(): DiveParams {
  return useTargetingStore((state) => state.dive);
}

export function useReleaseParams(): ReleaseParams {
  return useTargetingStore((state) => state.release);
}
